import { useEffect, useState } from "react";
import { LinkedInCallback } from "react-linkedin-login-oauth2";
import { useSearchParams } from "react-router-dom";
import useRequest from "../../api/useRequest";
import {
  LinkedInIntegrationsRequestFactory,
  PublicRequestFactory,
} from "../../api/requestFactory";
import { ERROR_CODES } from "../../api/errorCodes";
import * as Mixpanel from "../../services/mixpanel";

export const PUBLIC_CONNECT_RESULT_KEY = "linkedin_public_connect_result";

export const toPublicConnectResult = (err) => {
  const code = err?.response?.data?.code;

  if (
    code === ERROR_CODES.LINKEDIN_MIGRATE_ERROR_ACCOUNT ||
    code === ERROR_CODES.LINKEDIN_MIGRATE_ERROR_WORKSPACE
  ) {
    return { result: code, tempToken: err?.response?.data?.temp_token };
  }

  const message =
    err?.response?.data?.errors?.[0] ||
    "Failed to connect your LinkedIn account. Please try again.";
  return { result: { type: "connect_error", message } };
};

export const readPublicConnectResult = (linkId) => {
  try {
    const stored = JSON.parse(localStorage.getItem(PUBLIC_CONNECT_RESULT_KEY));
    return stored?.info?.id === linkId ? stored : null;
  } catch {
    return null;
  }
};

/**
 * Wrapper component that handles both popup and redirect OAuth flows
 * - For desktop: Uses LinkedInCallback from react-linkedin-login-oauth2 (popup flow)
 * - For mobile: Handles redirect flow by extracting code from URL and processing it
 */
export default function LinkedInCallbackHandler() {
  const [searchParams] = useSearchParams();
  const { requestMaker } = useRequest();
  const [isRedirectFlow, setIsRedirectFlow] = useState(false);
  const [processing, setProcessing] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const storedState = localStorage.getItem("linkedin_oauth_state");

    if (storedState) {
      // This is a redirect flow from mobile
      setIsRedirectFlow(true);
      handleRedirectFlow(storedState);
    } else {
      // This is a popup flow, let LinkedInCallback handle it
      setIsRedirectFlow(false);
      setProcessing(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // A successful connect closes the link, so the public page cannot refetch it; hand the outcome back instead.
  const connectPublicLink = async (publicLink, code, redirectUri, client_id) => {
    let outcome;
    try {
      await requestMaker(
        PublicRequestFactory.createLinkedInConnectRequest(
          publicLink.id,
          code,
          redirectUri,
          publicLink.workspaceId,
          client_id
        )
      );
      outcome = { result: "success" };
    } catch (err) {
      console.error(err);
      outcome = toPublicConnectResult(err);
    }

    localStorage.setItem(
      PUBLIC_CONNECT_RESULT_KEY,
      JSON.stringify({ ...outcome, info: publicLink })
    );
  };

  const handleRedirectFlow = async (storedState) => {
    try {
      const { state: savedState, returnUrl, publicLink } = JSON.parse(storedState);
      const code = searchParams.get("code");
      const urlState = searchParams.get("state");
      const errorParam = searchParams.get("error");

      // Clear stored state
      localStorage.removeItem("linkedin_oauth_state");

      // Check for errors from LinkedIn
      if (errorParam) {
        const errorDescription = searchParams.get("error_description") || "LinkedIn authentication failed";
        setError(errorDescription);
        setProcessing(false);

        // Redirect back after showing error
        setTimeout(() => {
          window.location.href = returnUrl || "/";
        }, 3000);
        return;
      }

      // Validate state to prevent CSRF
      if (urlState !== savedState) {
        setError("Invalid state parameter. Please try again.");
        setProcessing(false);
        setTimeout(() => {
          window.location.href = returnUrl || "/";
        }, 3000);
        return;
      }

      if (!code) {
        setError("No authorization code received. Please try again.");
        setProcessing(false);
        setTimeout(() => {
          window.location.href = returnUrl || "/";
        }, 3000);
        return;
      }

      const redirectUri = `${window.location.origin}/integrations/linkedin/callback`;
      const client_id = import.meta.env.VITE_LINKEDIN_CLIENT_ID_V3;

      // Invited users have no session, so they must use the public endpoint, not the logged-in one.
      if (publicLink) {
        await connectPublicLink(publicLink, code, redirectUri, client_id);
        window.location.href = returnUrl;
        return;
      }

      // Send code to backend
      await requestMaker(
        LinkedInIntegrationsRequestFactory.createNewLinkedInIntegrationRequest(
          code,
          redirectUri,
          client_id
        )
      );

      Mixpanel.track("LinkedIn Integration Completed", { flow: "mobile_redirect" });

      // Redirect back to where user came from
      window.location.href = returnUrl || "/";
    } catch (err) {
      console.error("LinkedIn redirect flow error:", err);
      Mixpanel.track("LinkedIn Integration Failed", {
        error: err?.response?.data?.errors?.[0] || err.message,
        flow: "mobile_redirect"
      });

      const errorMessage = err?.response?.data?.errors?.[0] ||
        "Something went wrong while connecting your LinkedIn account. Please try again.";

      setError(errorMessage);
      setProcessing(false);

      // Try to get return URL for redirect
      let returnUrl = "/";
      try {
        const storedState = localStorage.getItem("linkedin_oauth_state");
        if (storedState) {
          returnUrl = JSON.parse(storedState).returnUrl || "/";
          localStorage.removeItem("linkedin_oauth_state");
        }
      } catch (e) {
        // Ignore parse error
      }

      // Redirect back after showing error
      setTimeout(() => {
        window.location.href = returnUrl;
      }, 3000);
    }
  };

  // Show processing state for redirect flow
  if (isRedirectFlow) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-white">
        <div className="text-center p-8">
          {processing && !error && (
            <>
              <svg
                className="animate-spin h-10 w-10 text-blue-500 mx-auto mb-4"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                />
              </svg>
              <p className="text-gray-600 text-lg">Connecting your LinkedIn account...</p>
            </>
          )}
          {error && (
            <>
              <svg
                className="h-10 w-10 text-red-500 mx-auto mb-4"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
              <p className="text-red-600 text-lg mb-2">{error}</p>
              <p className="text-gray-500 text-sm">Redirecting you back...</p>
            </>
          )}
        </div>
      </div>
    );
  }

  // For popup flow, use the library's callback component
  return <LinkedInCallback />;
}
